import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  MessageSquare,
  Clock,
  AlertTriangle,
  Settings,
} from "lucide-react";
import {
  format,
  addDays,
  parseISO,
  differenceInMinutes,
  isBefore,
  isAfter,
  startOfDay,
} from "date-fns";
import { getBrowserTimezone, getTimezoneAbbr, formatInTz } from "@/lib/timezone";
import PatientConsultDrawer from "@/components/PatientConsultDrawer";

interface Appointment {
  id: string;
  patient_id: string;
  start_time: string;
  end_time: string;
  status: string;
  call_type: string;
  meeting_url: string | null;
  notes: string | null;
}

interface EnrichedAppointment extends Appointment {
  patient_first_name: string | null;
  patient_last_name: string | null;
  goal: string | null;
  conditions: string | null;
  allergies: string | null;
  medications: string | null;
}

interface ProviderTodayScheduleProps {
  onNavigateToAvailability?: () => void;
}

const SLOT_HEIGHT = 28; // px per 15-min slot
const DAY_START_HOUR = 7;
const DAY_END_HOUR = 21;
const TOTAL_SLOTS = (DAY_END_HOUR - DAY_START_HOUR) * 4; // 56 slots

const ProviderTodaySchedule = ({ onNavigateToAvailability }: ProviderTodayScheduleProps) => {
  const { user } = useAuth();
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const [appointments, setAppointments] = useState<EnrichedAppointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [drawerPatientId, setDrawerPatientId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const nowLineRef = useRef<HTMLDivElement>(null);

  const tz = useMemo(() => getBrowserTimezone(), []);
  const tzAbbr = useMemo(() => getTimezoneAbbr(tz), [tz]);
  const isToday = useMemo(
    () => format(selectedDate, "yyyy-MM-dd") === format(new Date(), "yyyy-MM-dd"),
    [selectedDate]
  );

  const dateStr = format(selectedDate, "yyyy-MM-dd");

  const fetchAppointments = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const dayStart = `${dateStr}T00:00:00`;
    const dayEnd = `${dateStr}T23:59:59`;

    const { data: appts } = await supabase
      .from("appointments")
      .select("id, patient_id, start_time, end_time, status, call_type, meeting_url, notes")
      .eq("provider_id", user.id)
      .gte("start_time", dayStart)
      .lte("start_time", dayEnd)
      .neq("status", "cancelled")
      .order("start_time");

    if (!appts || appts.length === 0) {
      setAppointments([]);
      setLoading(false);
      return;
    }

    const patientIds = [...new Set(appts.map((a) => a.patient_id))];
    const [profilesRes, assessmentsRes] = await Promise.all([
      supabase.from("profiles").select("user_id, first_name, last_name").in("user_id", patientIds),
      supabase
        .from("health_assessments")
        .select("user_id, goal, conditions, allergies, medications")
        .in("user_id", patientIds)
        .order("created_at", { ascending: false }),
    ]);

    const profileMap = new Map(
      profilesRes.data?.map((p) => [p.user_id, p]) || []
    );
    // Latest assessment per patient
    const assessmentMap = new Map<string, any>();
    assessmentsRes.data?.forEach((a) => {
      if (!assessmentMap.has(a.user_id)) assessmentMap.set(a.user_id, a);
    });

    const enriched: EnrichedAppointment[] = appts.map((a) => {
      const profile = profileMap.get(a.patient_id);
      const assessment = assessmentMap.get(a.patient_id);
      return {
        ...a,
        patient_first_name: profile?.first_name ?? null,
        patient_last_name: profile?.last_name ?? null,
        goal: assessment?.goal ?? null,
        conditions: assessment?.conditions ?? null,
        allergies: assessment?.allergies ?? null,
        medications: assessment?.medications ?? null,
      };
    });

    setAppointments(enriched);
    setLoading(false);
  }, [user, dateStr]);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  // Auto-scroll to current time
  useEffect(() => {
    if (isToday && nowLineRef.current) {
      nowLineRef.current.scrollIntoView({ block: "center", behavior: "smooth" });
    }
  }, [isToday, loading]);

  // Next upcoming appointment
  const now = new Date();
  const upNext = useMemo(
    () =>
      appointments.find(
        (a) =>
          isAfter(parseISO(a.end_time), now) &&
          a.status !== "completed" &&
          a.status !== "no_show"
      ),
    [appointments, now]
  );

  const minutesToNext = upNext ? differenceInMinutes(parseISO(upNext.start_time), now) : null;
  const canJoin = (a: Appointment) =>
    a.meeting_url &&
    differenceInMinutes(parseISO(a.start_time), new Date()) <= 5 &&
    isAfter(parseISO(a.end_time), new Date());

  // Map appointment to slot position
  const getSlotPosition = (isoTime: string) => {
    const d = parseISO(isoTime);
    const hours = d.getHours() - DAY_START_HOUR;
    const mins = d.getMinutes();
    return hours * 4 + Math.floor(mins / 15);
  };

  const nowSlot = useMemo(() => {
    const h = now.getHours() - DAY_START_HOUR;
    const m = now.getMinutes();
    return h * 4 + m / 15;
  }, [now]);

  const patientName = (a: EnrichedAppointment) =>
    a.patient_first_name
      ? `${a.patient_first_name} ${(a.patient_last_name || "").charAt(0)}.`
      : "Patient";

  const formatSlotTime = (slotIndex: number) => {
    const totalMinutes = (DAY_START_HOUR * 60) + (slotIndex * 15);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    const ampm = h >= 12 ? "PM" : "AM";
    const hour = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${hour}:${String(m).padStart(2, "0")} ${ampm}`;
  };

  return (
    <div className="space-y-4">
      {/* Header with date navigation */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <CalendarDays size={20} className="text-primary" />
          <div>
            <h2 className="font-display text-xl text-foreground">
              {isToday ? "Today" : format(selectedDate, "EEEE")}{" "}
              <span className="text-muted-foreground font-normal text-base">
                — {format(selectedDate, "MMMM d, yyyy")}
              </span>
            </h2>
            <p className="text-xs text-muted-foreground">Times shown in {tzAbbr}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => setSelectedDate((d) => addDays(d, -1))}
          >
            <ChevronLeft size={16} />
          </Button>
          {!isToday && (
            <Button variant="ghost" size="sm" className="text-xs" onClick={() => setSelectedDate(new Date())}>
              Today
            </Button>
          )}
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => setSelectedDate((d) => addDays(d, 1))}
          >
            <ChevronRight size={16} />
          </Button>
        </div>
      </div>

      {/* Up Next Card */}
      {isToday && upNext && (
        <Card className="border-primary/30 bg-primary/5">
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30 text-[10px]">
                    UP NEXT
                  </Badge>
                  {minutesToNext !== null && (
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Clock size={12} />
                      {minutesToNext <= 0
                        ? "Starting now"
                        : minutesToNext < 60
                          ? `in ${minutesToNext} min`
                          : `in ${Math.floor(minutesToNext / 60)}h ${minutesToNext % 60}m`}
                    </span>
                  )}
                </div>
                <p className="font-semibold text-foreground truncate">{patientName(upNext)}</p>
                <p className="text-sm text-muted-foreground">
                  {formatInTz(upNext.start_time, tz, "h:mm a")} –{" "}
                  {formatInTz(upNext.end_time, tz, "h:mm a")}
                  <span className="mx-1.5">·</span>
                  <span className="inline-flex items-center gap-1"><MessageSquare size={12} /> Async check-in</span>
                </p>

                {/* Quick-prep info */}
                {(upNext.goal || upNext.conditions || upNext.allergies || upNext.medications) && (
                  <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                    {upNext.goal && (
                      <div>
                        <span className="text-muted-foreground">Goal:</span>{" "}
                        <span className="text-foreground">{upNext.goal}</span>
                      </div>
                    )}
                    {upNext.conditions && (
                      <div>
                        <span className="text-muted-foreground">Conditions:</span>{" "}
                        <span className="text-foreground">{upNext.conditions}</span>
                      </div>
                    )}
                    {upNext.allergies && upNext.allergies.toLowerCase() !== "none" && (
                      <div className="flex items-center gap-1">
                        <AlertTriangle size={10} className="text-destructive shrink-0" />
                        <span className="text-destructive">{upNext.allergies}</span>
                      </div>
                    )}
                    {upNext.medications && upNext.medications.toLowerCase() !== "none" && (
                      <div>
                        <span className="text-muted-foreground">Meds:</span>{" "}
                        <span className="text-foreground">{upNext.medications}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-2 shrink-0">
                {canJoin(upNext) && (
                  <Button size="sm" asChild>
                    <a href={upNext.meeting_url!} className="gap-1.5">
                      <MessageSquare size={14} /> Open Inbox
                    </a>
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setDrawerPatientId(upNext.patient_id);
                    setDrawerOpen(true);
                  }}
                >
                  View Chart
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Timeline */}
      <Card className="border-border bg-card overflow-hidden">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-48 text-muted-foreground text-sm">
              Loading schedule…
            </div>
          ) : (
            <div
              ref={scrollRef}
              className="overflow-y-auto max-h-[calc(100vh-22rem)] relative"
            >
              <div style={{ height: TOTAL_SLOTS * SLOT_HEIGHT }} className="relative">
                {/* Time labels + grid lines */}
                {Array.from({ length: TOTAL_SLOTS }, (_, i) => {
                  const isHourMark = i % 4 === 0;
                  return (
                    <div
                      key={i}
                      className={`absolute left-0 right-0 flex items-start ${
                        isHourMark ? "border-t border-border" : i % 2 === 0 ? "border-t border-border/30" : ""
                      }`}
                      style={{ top: i * SLOT_HEIGHT, height: SLOT_HEIGHT }}
                    >
                      {isHourMark && (
                        <span className="w-16 text-[10px] text-muted-foreground pl-2 pt-0.5 shrink-0">
                          {formatSlotTime(i)}
                        </span>
                      )}
                    </div>
                  );
                })}

                {/* Current time indicator */}
                {isToday && nowSlot >= 0 && nowSlot <= TOTAL_SLOTS && (
                  <div
                    ref={nowLineRef}
                    className="absolute left-14 right-0 z-20 flex items-center pointer-events-none"
                    style={{ top: nowSlot * SLOT_HEIGHT }}
                  >
                    <div className="w-2.5 h-2.5 rounded-full bg-destructive -ml-1" />
                    <div className="flex-1 h-px bg-destructive" />
                  </div>
                )}

                {/* Appointment blocks */}
                {appointments.map((appt) => {
                  const startSlot = getSlotPosition(appt.start_time);
                  const endSlot = getSlotPosition(appt.end_time);
                  const slotSpan = Math.max(endSlot - startSlot, 1);
                  const isPast = isBefore(parseISO(appt.end_time), now);
                  const isCompleted = appt.status === "completed";
                  const isNoShow = appt.status === "no_show";

                  return (
                    <button
                      key={appt.id}
                      onClick={() => {
                        setDrawerPatientId(appt.patient_id);
                        setDrawerOpen(true);
                      }}
                      className={`absolute left-16 right-2 rounded-lg px-3 py-1.5 text-left transition-colors cursor-pointer z-10 ${
                        isCompleted
                          ? "bg-green-500/10 border border-green-500/30 hover:bg-green-500/20"
                          : isNoShow
                            ? "bg-red-500/10 border border-red-500/30 hover:bg-red-500/20"
                            : isPast
                              ? "bg-muted border border-border hover:bg-muted/80"
                              : "bg-primary/10 border border-primary/30 hover:bg-primary/20"
                      }`}
                      style={{
                        top: startSlot * SLOT_HEIGHT + 1,
                        height: slotSpan * SLOT_HEIGHT - 2,
                      }}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm text-foreground truncate">
                              {patientName(appt)}
                            </span>
                            <MessageSquare size={12} className="text-muted-foreground shrink-0" />
                            {isCompleted && (
                              <Badge variant="outline" className="text-[9px] px-1 py-0 bg-green-500/10 text-green-700 border-green-500/30">
                                Done
                              </Badge>
                            )}
                            {isNoShow && (
                              <Badge variant="outline" className="text-[9px] px-1 py-0 bg-red-500/10 text-red-700 border-red-500/30">
                                No Show
                              </Badge>
                            )}
                          </div>
                          {slotSpan >= 2 && (
                            <p className="text-[11px] text-muted-foreground truncate mt-0.5">
                              {formatInTz(appt.start_time, tz, "h:mm")} – {formatInTz(appt.end_time, tz, "h:mm a")}
                              {appt.goal && <span className="ml-1.5">· {appt.goal}</span>}
                            </p>
                          )}
                        </div>
                        {canJoin(appt) && (
                          <Button
                            size="sm"
                            className="h-7 text-xs shrink-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              window.location.href = appt.meeting_url!;
                            }}
                          >
                            <MessageSquare size={12} className="mr-1" /> Open
                          </Button>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Empty state */}
          {!loading && appointments.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <CalendarDays size={40} className="text-muted-foreground/30 mb-3" />
              <p className="text-foreground font-medium mb-1">
                No appointments {isToday ? "today" : `on ${format(selectedDate, "MMM d")}`}
              </p>
              <p className="text-sm text-muted-foreground mb-4">
                Your schedule is clear.
              </p>
              {onNavigateToAvailability && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onNavigateToAvailability}
                  className="gap-1.5"
                >
                  <Settings size={14} /> Manage availability
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Consult drawer */}
      <PatientConsultDrawer
        patientId={drawerPatientId}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
      />
    </div>
  );
};

export default ProviderTodaySchedule;
